import { formatCalendarDateIso, formatDate, parseUtcDate } from './dateFormat';

/** Minimal cycle shape needed to dovetail a suggested quarter range. */
export type ExistingReviewCycleDates = {
  name: string;
  startDate: string;
  endDate: string;
};

export type SuggestedReviewCycleDates =
  | {
      ok: true;
      startDate: Date;
      endDate: Date;
      employeeCutoff: Date;
      /** True when start was moved later to follow an extended prior cycle. */
      adjusted: boolean;
      adjustedBecauseOfName?: string;
      adjustedBecauseOfEnd?: Date;
    }
  | {
      ok: false;
      message: string;
    };

function addUtcDays(date: Date, days: number): Date {
  const next = new Date(date.getTime());
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function utcDayMs(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/**
 * Builds start/end/cutoff for a suggested quarter, dovetailing after any existing
 * cycle that overlaps the quarter (same behaviour as backend auto-create).
 */
export function buildSuggestedReviewCycleDates(
  quarterStart: Date,
  quarterEnd: Date,
  existingCycles: ExistingReviewCycleDates[],
  deadlineDaysBeforeEnd = 7
): SuggestedReviewCycleDates {
  const qStartMs = utcDayMs(quarterStart);
  const qEndMs = utcDayMs(quarterEnd);

  let blocking: { name: string; end: Date } | null = null;
  for (const cycle of existingCycles) {
    const start = parseUtcDate(cycle.startDate);
    const end = parseUtcDate(cycle.endDate);
    if (!start || !end) continue;

    const startMs = utcDayMs(start);
    const endMs = utcDayMs(end);
    const overlaps = startMs <= qEndMs && endMs >= qStartMs;
    if (!overlaps) continue;

    if (!blocking || endMs > utcDayMs(blocking.end)) {
      blocking = { name: cycle.name, end };
    }
  }

  let startDate = quarterStart;
  let adjusted = false;
  if (blocking) {
    const dovetailStart = addUtcDays(blocking.end, 1);
    if (utcDayMs(dovetailStart) > qEndMs) {
      return {
        ok: false,
        message:
          `${blocking.name} runs through ${formatDate(blocking.end)} and covers this whole quarter. ` +
          `Shorten or close that cycle before creating the next one.`,
      };
    }
    if (utcDayMs(dovetailStart) > qStartMs) {
      startDate = dovetailStart;
      adjusted = true;
    }
  }

  const endDate = quarterEnd;
  let employeeCutoff = addUtcDays(endDate, -deadlineDaysBeforeEnd);
  if (utcDayMs(employeeCutoff) < utcDayMs(startDate)) {
    employeeCutoff = startDate;
  }
  if (utcDayMs(employeeCutoff) > utcDayMs(endDate)) {
    employeeCutoff = endDate;
  }

  return {
    ok: true,
    startDate,
    endDate,
    employeeCutoff,
    adjusted,
    adjustedBecauseOfName: adjusted ? blocking?.name : undefined,
    adjustedBecauseOfEnd: adjusted ? blocking?.end : undefined,
  };
}

/** Info line when a quick suggestion had to start after an extended prior cycle. */
export function dovetailAdjustmentMessage(
  result: Extract<SuggestedReviewCycleDates, { ok: true }>
): string | null {
  if (!result.adjusted || !result.adjustedBecauseOfName || !result.adjustedBecauseOfEnd) {
    return null;
  }
  return (
    `Start date set to ${formatDate(result.startDate)} so this cycle follows ` +
    `${result.adjustedBecauseOfName} (ends ${formatDate(result.adjustedBecauseOfEnd)}).`
  );
}

/** Compare two calendar dates as yyyy-MM-dd in UTC (for tests / callers). */
export function toUtcCalendarIso(date: Date): string {
  return formatCalendarDateIso(date);
}
